import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import * as api from 'api/api'
import { Attachment, CatalogItem, CatalogKind, EquipmentLine, LabTestLine, MaterialLine, Note, Report, ReportContent, Section, StaffLine, WorkLine } from 'api/types'
import { useAuth } from 'auth/AuthContext'
import { useConfirm } from 'components/Confirm'
import { Icon } from 'components/Icon'
import { useProject } from 'components/Layout'
import { ReportLifecycleTracker } from 'components/reports/report-lifecycle-tracker'
import { ReturnedBanner } from 'components/reports/ReturnedBanner'
import { ReturnReportDialog } from 'components/ReturnReportDialog'
import { useToast } from 'components/Toast'
import { Button, Card, CollapsibleCard, ErrorState, Modal, Spinner, StatusBadge, TextAreaField } from 'components/ui'
import {
  AUDIT_LABELS,
  ROLE_LABELS,
  SECTION_LABELS,
  SUBCONTRACTOR_STATUS_LABELS,
  WEATHER_OPTIONS,
  WEEK_DAYS,
  WORKFLOW_LABELS,
  addDays,
  formatDate,
  formatDateTime,
  parseIso,
  weatherLabel,
  weatherValue,
} from 'lib/format'
import { t } from 'lib/i18n'
import { errorMessage, useAction, useAsync } from 'lib/hooks'

import { AttachmentActions, AttachmentChips, Gallery, PreviewModal, UploadButton } from './Attachments'
import { contentOf, normalize, sectionFillStates, SECTION_ICONS, submitChecklist } from './checklist'
import { LinesEditor } from './LinesEditor'
import { VisitLines } from './VisitLines'

const commentInitials = (name?: string | null) =>
  (name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || t('؟', '?')

const weekdayFromDate = (iso: string) => WEEK_DAYS[(parseIso(iso).getUTCDay() + 1) % 7]

const CONSTANT_CREW = ['مدير المشروع', 'مهندس مدني', 'مهندس كهرباء', 'مهندس ميكانيكي', 'حاسب كميات', 'مراقب ابنية', 'حارس']

const Datalists = ({ catalog, hiddenJobTitles }: { catalog: CatalogItem[]; hiddenJobTitles: string[] }) => {
  const list = (kind: CatalogKind, fixed?: boolean) =>
    catalog.filter((item) => item.kind === kind && (fixed === undefined || item.isFixed === fixed))
  return (
    <>
      <datalist id="dl-job">
        {list('JOB_TITLE', false)
          .filter((item) => !hiddenJobTitles.includes(item.label))
          .map((item) => (
            <option key={item.id} value={item.label} />
          ))}
      </datalist>
      <datalist id="dl-material">
        {list('MATERIAL').map((item) => (
          <option key={item.id} value={item.label} />
        ))}
      </datalist>
      <datalist id="dl-equipment">
        {list('EQUIPMENT').map((item) => (
          <option key={item.id} value={item.label} />
        ))}
      </datalist>
      <datalist id="dl-test">
        {list('TEST_TYPE').map((item) => (
          <option key={item.id} value={item.label} />
        ))}
      </datalist>
      <datalist id="dl-weather">
        {WEATHER_OPTIONS.map((option) => (
          <option key={option} value={weatherLabel(option)} />
        ))}
      </datalist>
    </>
  )
}

const NotedTransitionModal = ({
  title,
  confirmLabel,
  busy,
  onConfirm,
  onClose,
}: {
  title: string
  confirmLabel: string
  busy: boolean
  onConfirm: (note: string) => void
  onClose: () => void
}) => {
  const [note, setNote] = useState('')
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('إلغاء', 'Cancel')}</Button>
          <Button variant="danger" busy={busy} disabled={!note.trim()} onClick={() => onConfirm(note.trim())}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <TextAreaField
        label={t('السبب / الملاحظة', 'Reason / note')}
        hint={t('ستظهر الملاحظة لمهندس الموقع في أعلى التقرير', 'The site engineer will see this note at the top of the report')}
        rows={4}
        value={note}
        onChange={(event) => setNote(event.target.value)}
        required
      />
    </Modal>
  )
}

export const ReportPage = () => {
  const { reportId = '' } = useParams()
  const { project } = useProject()
  const { user } = useAuth()
  const navigate = useNavigate()
  const notify = useToast()
  const { busy, run } = useAction()
  const confirm = useConfirm()

  const { data: loaded, error, loading, reload } = useAsync(() => api.fetchReport(reportId), [reportId])
  const { data: catalog = [] } = useAsync(() => api.fetchCatalog(project.id), [project.id])
  const { data: subcontractors = [] } = useAsync(api.fetchSubcontractors, [])

  const [report, setReport] = useState<Report | null>(null)
  const [content, setContent] = useState<ReportContent | null>(null)
  const [dirty, setDirty] = useState(false)
  const dirtyRef = useRef(false)
  const [preview, setPreview] = useState<Attachment | null>(null)
  const [uploading, setUploading] = useState<string | null>(null)
  const [dialog, setDialog] = useState<'return' | 'reopen' | null>(null)
  const [noteText, setNoteText] = useState('')

  const markDirty = (value: boolean) => {
    dirtyRef.current = value
    setDirty(value)
  }

  const apply = useCallback((next: Report, replaceContent = true) => {
    setReport(next)
    if (replaceContent) {
      setContent(contentOf(next))
      dirtyRef.current = false
      setDirty(false)
    }
  }, [])

  useEffect(() => {
    if (loaded) apply(loaded)
  }, [loaded, apply])

  useEffect(() => {
    if (!dirty) return undefined
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  const fixedTitles = useMemo(() => {
    const saved = catalog
      .filter((item) => item.kind === 'JOB_TITLE' && item.isFixed)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((item) => item.label)
    return saved.length ? saved : CONSTANT_CREW
  }, [catalog])

  const subcontractorOptions = subcontractors.map((item) => ({
    label: item.status === 'ACTIVE' ? item.name : `${item.name} (${SUBCONTRACTOR_STATUS_LABELS[item.status]})`,
    value: item.id,
  }))

  const save = useCallback(
    async (quiet = false): Promise<Report | undefined> => {
      if (!report || !content) return undefined
      const saved = await run('save', () => api.saveReport(report.id, normalize(content)), quiet ? undefined : t('تم حفظ التقرير', 'Report saved'))
      if (saved) apply(saved)
      return saved
    },
    [report, content, run, apply]
  )

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault()
        if (dirtyRef.current && report?.permissions.canEdit) save()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [save, report])

  if (loading && !report) return <Spinner />
  if (error && !report) return <ErrorState message={error} onRetry={reload} />
  if (!report || !content) return null

  const { permissions } = report
  const readOnly = !permissions.canEdit
  const normalized = normalize(content)
  const checklist = submitChecklist(normalized, report.attachments)
  const ready = checklist.every((item) => item.done)

  const change = <K extends keyof ReportContent>(key: K, value: ReportContent[K]) => {
    setContent((current) => (current ? { ...current, [key]: value } : current))
    markDirty(true)
  }

  const refresh = async () => {
    try {
      const next = await api.fetchReport(report.id)
      apply(next, !dirtyRef.current)
    } catch (caught) {
      notify(errorMessage(caught), 'error')
    }
  }

  const uploadTo = async (section: Section, files: File[], lineId?: string) => {
    if (dirtyRef.current && permissions.canEdit) {
      const saved = await save(true)
      if (!saved) return
    }
    setUploading(lineId ?? section)
    const done = await run('upload', () => api.uploadAttachments(report.id, section, files, { lineId }), t('تم رفع الملفات', 'Files uploaded'))
    setUploading(null)
    if (done) await refresh()
  }

  const attachmentActions: AttachmentActions = {
    canRemove: (attachment) =>
      permissions.canEdit ||
      (attachment.section === 'SUPERVISION' && permissions.canAddNote && (report.myRole === 'ADMIN' || attachment.uploadedByName === user?.name)),
    remove: async (attachment) => {
      if (!(await confirm(t(`حذف المرفق «${attachment.originalName}»؟`, `Delete the attachment “${attachment.originalName}”?`), { confirmLabel: t('حذف', 'Delete') }))) return
      await api.deleteAttachment(attachment.id)
      notify(t('تم حذف المرفق', 'Attachment deleted'))
      await refresh()
    },
    upload: uploadTo,
  }

  const sectionFiles = (section: Section) => report.attachments.filter((item) => item.section === section && !item.lineId)
  const lineFiles = (section: Section) => report.attachments.filter((item) => item.section === section)

  const go = async (offset: number) => {
    if (
      dirtyRef.current &&
      !(await confirm(t('لديك تعديلات غير محفوظة. هل تريد المتابعة دون حفظ؟', 'You have unsaved changes. Continue without saving?'), {
        confirmLabel: t('متابعة', 'Continue'),
        tone: 'primary',
      }))
    )
      return
    const id = await run('nav', () => api.findReportByDate(project.id, addDays(report.reportDate, offset)))
    if (id) navigate(`/projects/${project.id}/reports/${id}`)
    else if (id === null) notify(t('التقرير غير متاح', 'Report is not available'), 'info')
  }

  const submit = async () => {
    if (dirtyRef.current) {
      const saved = await save(true)
      if (!saved) return
    }
    const next = await run('submit', () => api.transitionReport('submitReport', report.id), t('تم إرسال التقرير للاعتماد', 'Report submitted for approval'))
    if (next) apply(next)
  }

  const approve = async () => {
    if (
      !(await confirm(
        t(
          `اعتماد التقرير ${report.code}؟ لن يمكن تعديله بعد الاعتماد إلا بإعادة فتحه.`,
          `Approve report ${report.code}? It cannot be edited after approval unless it is reopened.`
        ),
        { confirmLabel: t('اعتماد', 'Approve'), tone: 'primary' }
      ))
    )
      return
    const next = await run('approve', () => api.transitionReport('approveReport', report.id))
    if (next) {
      apply(next)
      notify(t('تم اعتماد التقرير', 'Report approved'), 'success', next.preparedByName ? [t(`تم إعلام ${next.preparedByName}.`, `${next.preparedByName} has been notified.`)] : undefined)
    }
  }

  const notedTransition = async (note: string) => {
    if (dialog !== 'reopen') return
    const next = await run('reopen', () => api.notedTransition('reopenReport', report.id, note), t('تمت إعادة فتح التقرير', 'Report reopened'))
    if (next) {
      apply(next)
      setDialog(null)
    }
  }

  const confirmReturn = async (note: string, sections: Section[]) => {
    const next = await run('return', () => api.notedTransition('returnReport', report.id, note))
    if (next) {
      await Promise.all(sections.map((section) => api.addSupervisionNote(report.id, note, section)))
      notify(t('أصبح التقرير للمراجعة', 'The report is for review'))
      setDialog(null)
      await refresh()
    }
  }

  const addNote = async () => {
    const next = await run('note', () => api.addSupervisionNote(report.id, noteText.trim()), t('تمت إضافة الملاحظة', 'Note added'))
    if (next) {
      apply(next, !dirtyRef.current)
      setNoteText('')
    }
  }

  const removeNote = async (id: string) => {
    if (!(await confirm(t('حذف هذه الملاحظة؟', 'Delete this note?'), { confirmLabel: t('حذف', 'Delete') }))) return
    const next = await run(`note-${id}`, () => api.deleteSupervisionNote(id), t('تم حذف الملاحظة', 'Note deleted'))
    if (next) apply(next, !dirtyRef.current)
  }

  const canDeleteNote = (authorId: string) =>
    report.myRole === 'ADMIN' ||
    (authorId === user?.id && (permissions.canEdit || (report.myRole === 'SUPERVISOR' && permissions.canAddNote)))

  const generalComplete = Boolean(content.weather)

  const sections = sectionFillStates(normalized, report.notes)

  const filledOf = (section: Section) => sections.find((item) => item.section === section)?.filled ?? false

  const completenessBadge = (complete: boolean) => (
    <span className={complete ? 'badge badge--complete' : 'badge badge--new'}>{complete ? t('مكتمل', 'Complete') : t('فارغ', 'Empty')}</span>
  )

  const supervisionNotes = report.notes.filter((note) => note.section === 'SUPERVISION')

  const commentGroups: [Section, Note[]][] = []
  report.notes.forEach((note) => {
    let group = commentGroups.find(([section]) => section === note.section)
    if (!group) {
      group = [note.section, []]
      commentGroups.push(group)
    }
    group[1].push(note)
  })

  const otherStaff = content.staff.filter((line) => !fixedTitles.includes(line.title))
  const fixedStaff = content.staff.filter((line) => fixedTitles.includes(line.title))

  return (
    <div className="page report">
      <Datalists catalog={catalog} hiddenJobTitles={fixedTitles} />

      <div className="report__head">
        <div className="report__title">
          <div className="report__nav">
            <Button
              size="sm"
              variant="ghost"
              icon="chevronRight"
              disabled={report.reportDate <= project.startDate || busy === 'nav'}
              onClick={() => go(-1)}
            >
              {t('اليوم السابق', 'Previous day')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={report.reportDate >= project.endDate || busy === 'nav'}
              onClick={() => go(1)}
            >
              {t('اليوم التالي', 'Next day')}
              <Icon name="chevronLeft" />
            </Button>
          </div>
          <h1>
            {t('تقرير عمل يومي', 'Daily site report')} <span className="code">{report.code}</span>
          </h1>
          <p className="report__date">
            {weekdayFromDate(report.reportDate)} · {formatDate(report.reportDate)}
            <StatusBadge status={report.status} />
            <span className="chip">{WORKFLOW_LABELS[report.workflow]}</span>
          </p>
        </div>
        <div className="toolbar">
          {permissions.canEdit ? (
            <Button variant={dirty ? 'primary' : 'secondary'} icon="save" busy={busy === 'save'} disabled={!dirty} onClick={() => save()}>
              {dirty ? t('حفظ', 'Save') : t('محفوظ', 'Saved')}
            </Button>
          ) : null}
          {permissions.canSubmit ? (
            <Button
              variant="success"
              icon="send"
              busy={busy === 'submit'}
              onClick={submit}
              title={ready ? undefined : t('أكمل المتطلبات قبل الإرسال', 'Complete the requirements before you submit')}
            >
              {t('إرسال للاعتماد', 'Submit')}
            </Button>
          ) : null}
          {permissions.canApprove ? (
            <Button variant="success" icon="check" busy={busy === 'approve'} onClick={approve}>
              {t('اعتماد', 'Approve')}
            </Button>
          ) : null}
          {permissions.canReturn ? (
            <Button variant="danger" icon="undo" onClick={() => setDialog('return')}>
              {t('إرسال للمراجعة', 'Send for review')}
            </Button>
          ) : null}
          {permissions.canReopen ? (
            <Button icon="unlock" onClick={() => setDialog('reopen')}>
              {t('إعادة فتح', 'Reopen')}
            </Button>
          ) : null}
        </div>
      </div>

      <ReportLifecycleTracker report={report} />

      <ReturnedBanner report={report} />
      {report.status === 'MISSING' ? (
        <div className="alert alert--error" role="status">
          <Icon name="alert" />
          <strong>{t('هذا التقرير مفقود — لم يتم إدخال أي بيانات لهذا اليوم.', 'This report is missing — nothing was entered for this day.')}</strong>
        </div>
      ) : null}
      {readOnly && report.workflow === 'SUBMITTED' && !permissions.canApprove ? (
        <div className="alert alert--success" role="status">
          <Icon name="check" />
          <div>
            <strong>{t('تم إرسال التقرير للاعتماد', 'Report submitted for approval')}</strong>
            <p>
              {report.submittedAt
                ? t(
                    `أُرسل ${report.code} الساعة ${formatDateTime(report.submittedAt)}. ستصلك إشعار فور اعتماده أو إرساله للمراجعة.`,
                    `${report.code} was submitted at ${formatDateTime(report.submittedAt)}. You will be notified when it is approved or sent for review.`
                  )
                : t(
                    `أُرسل ${report.code}. ستصلك إشعار فور اعتماده أو إرساله للمراجعة.`,
                    `${report.code} was submitted. You will be notified when it is approved or sent for review.`
                  )}
            </p>
          </div>
        </div>
      ) : null}
      {readOnly && report.workflow === 'SUBMITTED' && !permissions.canApprove ? (
        <div className="alert alert--info" role="status">
          <Icon name="lock" />
          <strong>
            {t('التقرير بانتظار اعتماد مدير المشروع ولا يمكن تعديله حالياً.', 'This report is waiting for approval by the project manager and cannot be edited right now.')}
          </strong>
        </div>
      ) : null}

      <nav className="section-nav" aria-label={t('أقسام التقرير', 'Report sections')}>
        {sections.map((item) => (
          <a key={item.id} href={`#${item.id}`} className={item.filled ? 'is-filled' : undefined}>
            {item.filled ? <Icon name="check" size={14} /> : null}
            {SECTION_LABELS[item.section]}
          </a>
        ))}
      </nav>

      <div className="report__layout">
        <div className="report__main">
          <CollapsibleCard title={t('معلومات عامة', 'General information')} icon="file" id="sec-general" headerEnd={completenessBadge(generalComplete)}>
            <dl className="info-grid">
              <div>
                <dt>{t('المشروع', 'Project')}</dt>
                <dd>{project.name}</dd>
              </div>
              <div>
                <dt>{t('رقم العطاء', 'Tender no.')}</dt>
                <dd>{project.tenderNumber || '—'}</dd>
              </div>
              <div>
                <dt>{t('الإشراف', 'Supervision')}</dt>
                <dd>{project.consultant || '—'}</dd>
              </div>
              <div>
                <dt>
                  <label htmlFor="weather">
                    {t('حالة الجو', 'Weather')} <span className="field__required">*</span>
                  </label>
                </dt>
                <dd>
                  {readOnly ? (
                    content.weather ? weatherLabel(content.weather) : '—'
                  ) : (
                    <input
                      id="weather"
                      className="input"
                      list="dl-weather"
                      value={content.weather ? weatherLabel(content.weather) : ''}
                      placeholder={t('مثال: مشمس', 'e.g. Sunny')}
                      onChange={(event) => change('weather', weatherValue(event.target.value))}
                    />
                  )}
                </dd>
              </div>
            </dl>
          </CollapsibleCard>

          <CollapsibleCard title={SECTION_LABELS.STAFF} icon="users" id="sec-staff" headerEnd={completenessBadge(filledOf('STAFF'))}>
            <h3 className="subhead">{t('كوادر أخرى (عمالة، سائقون…)', 'Other crew (labor, drivers…)')}</h3>
            <LinesEditor<StaffLine>
              lines={otherStaff}
              columns={[
                { key: 'title', label: t('المسمى', 'Job title'), list: 'dl-job', placeholder: t('مثال: عمالة', 'e.g. Labor') },
                { key: 'count', label: t('العدد', 'Count'), size: 'sm', type: 'number' },
                { key: 'name', label: t('الاسم / ملاحظة', 'Name / note') },
                { key: 'subcontractorId', label: t('المقاول الفرعي', 'Subcontractor'), type: 'select', options: subcontractorOptions },
              ]}
              makeLine={() => ({ count: 1, name: '', title: '' })}
              addLabel={t('إضافة كادر', 'Add crew')}
              emptyText={t('لا توجد كوادر إضافية', 'No other crew')}
              readOnly={readOnly}
              onChange={(next) => change('staff', [...fixedStaff, ...next])}
            />
            <AttachmentChips attachments={sectionFiles('STAFF')} actions={attachmentActions} onPreview={setPreview} />
          </CollapsibleCard>

          <CollapsibleCard
            title={SECTION_LABELS.LAB_TESTS}
            icon="list"
            id="sec-lab"
            headerEnd={
              <>
                {completenessBadge(filledOf('LAB_TESTS'))}
                <span className="muted small">{t('كل فحص يحتاج نتيجة المختبر كمرفق قبل الإرسال', 'Each test needs a lab-result attachment before submit')}</span>
              </>
            }
          >
            <LinesEditor<LabTestLine>
              lines={content.labTests}
              columns={[
                { key: 'testType', label: t('نوع الفحص', 'Test type'), list: 'dl-test' },
                { key: 'location', label: t('الموقع / العنصر', 'Location / element') },
                { key: 'result', label: t('النتيجة', 'Result') },
              ]}
              makeLine={() => ({ location: '', result: '', testType: '' })}
              addLabel={t('إضافة فحص', 'Add test')}
              emptyText={t('لا توجد فحوصات مخبرية لهذا اليوم', 'No lab tests for this day')}
              readOnly={readOnly}
              onChange={(next) => change('labTests', next)}
              section="LAB_TESTS"
              attachments={lineFiles('LAB_TESTS')}
              attachmentActions={attachmentActions}
              attachmentRequired
              uploadingLine={uploading}
              onPreview={setPreview}
              onUploadLine={(line, files) => uploadTo('LAB_TESTS', files, line.id)}
            />
          </CollapsibleCard>

          <CollapsibleCard
            title={SECTION_LABELS.MATERIALS}
            icon="box"
            id="sec-materials"
            headerEnd={
              <>
                {completenessBadge(filledOf('MATERIALS'))}
                <span className="muted small">{t('كل مادة تحتاج سند توريد أو صورة كمرفق قبل الإرسال', 'Each material needs a delivery note or photo before submit')}</span>
              </>
            }
          >
            <LinesEditor<MaterialLine>
              lines={content.materials}
              columns={[
                { key: 'name', label: t('المادة', 'Material'), list: 'dl-material', size: 'lg' },
                { key: 'quantity', label: t('الكمية', 'Quantity'), placeholder: t('مثال: 4 متر', 'e.g. 4 m'), size: 'sm' },
              ]}
              makeLine={() => ({ name: '', quantity: '' })}
              addLabel={t('إضافة مادة', 'Add material')}
              emptyText={t('لا توجد مواد موردة لهذا اليوم', 'No materials delivered today')}
              readOnly={readOnly}
              onChange={(next) => change('materials', next)}
              section="MATERIALS"
              attachments={lineFiles('MATERIALS')}
              attachmentActions={attachmentActions}
              attachmentRequired
              uploadingLine={uploading}
              onPreview={setPreview}
              onUploadLine={(line, files) => uploadTo('MATERIALS', files, line.id)}
            />
          </CollapsibleCard>

          <CollapsibleCard
            title={SECTION_LABELS.EQUIPMENT}
            icon="truck"
            id="sec-equipment"
            headerEnd={completenessBadge(filledOf('EQUIPMENT'))}
          >
            <LinesEditor<EquipmentLine>
              lines={content.equipment}
              columns={[
                { key: 'name', label: t('الآلة', 'Machine'), list: 'dl-equipment', size: 'lg' },
                { key: 'count', label: t('العدد', 'Count'), size: 'sm', type: 'number' },
              ]}
              makeLine={() => ({ count: 1, name: '' })}
              addLabel={t('إضافة آلة', 'Add equipment')}
              emptyText={t('لا توجد معدات في الموقع', 'No equipment on site')}
              readOnly={readOnly}
              onChange={(next) => change('equipment', next)}
            />
          </CollapsibleCard>

          <CollapsibleCard
            title={SECTION_LABELS.WORKS}
            icon="edit"
            id="sec-works"
            headerEnd={
              <>
                {completenessBadge(filledOf('WORKS'))}
                {!readOnly ? (
                  <UploadButton
                    label={t('إرفاق صور الموقع', 'Attach site photos')}
                    busy={uploading === 'WORKS'}
                    onFiles={(files) => uploadTo('WORKS', files)}
                  />
                ) : null}
              </>
            }
          >
            <LinesEditor<WorkLine>
              lines={content.works}
              columns={[{ key: 'text', label: t('وصف العمل', 'Work description'), type: 'textarea' }]}
              makeLine={() => ({ text: '' })}
              addLabel={t('إضافة بند عمل', 'Add work item')}
              emptyText={t('لم يُوصف أي عمل بعد', 'No work described yet')}
              readOnly={readOnly}
              onChange={(next) => change('works', next)}
            />
            <Gallery attachments={sectionFiles('WORKS')} actions={attachmentActions} onPreview={setPreview} />
            {sectionFiles('WORKS').length ? (
              <p className="muted small">{t('تظهر صور الموقع (JPG/PNG) في ملحق الصور ضمن ملف Excel وPDF.', 'Site photos (JPG/PNG) appear in the photo appendix of the Excel and PDF files.')}</p>
            ) : null}
          </CollapsibleCard>

          <CollapsibleCard title={SECTION_LABELS.VISITS} icon="mapPin" id="sec-visits" headerEnd={completenessBadge(filledOf('VISITS'))}>
            <VisitLines
              lines={content.visits}
              recordings={report.attachments.filter((item) => item.section === 'VISITS' && item.lineId)}
              readOnly={readOnly}
              uploadingId={uploading}
              onChange={(next) => change('visits', next)}
              onRecorded={(line, file) => {
                if (line.id) uploadTo('VISITS', [file], line.id)
              }}
              onRemoveRecording={(attachment) => attachmentActions.remove(attachment)}
            />
          </CollapsibleCard>

          <CollapsibleCard
            title={SECTION_LABELS.SUPERVISION}
            icon="clipboardCheck"
            id="sec-notes"
            headerEnd={
              <>
                {completenessBadge(filledOf('SUPERVISION'))}
                {permissions.canAddNote ? (
                  <UploadButton
                    label={t('إرفاق ملف', 'Attach file')}
                    busy={uploading === 'SUPERVISION'}
                    onFiles={(files) => uploadTo('SUPERVISION', files)}
                  />
                ) : null}
              </>
            }
          >
            {supervisionNotes.length ? (
              <ul className="notes">
                {supervisionNotes.map((note) => (
                  <li key={note.id}>
                    <p>{note.text}</p>
                    <footer>
                      <span>
                        {note.authorName} · {formatDateTime(note.createdAt)}
                      </span>
                      {canDeleteNote(note.authorId) ? (
                        <button
                          type="button"
                          className="link-button link-button--danger"
                          disabled={busy === `note-${note.id}`}
                          onClick={() => removeNote(note.id)}
                        >
                          {t('حذف', 'Delete')}
                        </button>
                      ) : null}
                    </footer>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="lines__empty">{t('لا توجد ملاحظات إشراف', 'No supervision notes')}</p>
            )}
            <AttachmentChips attachments={sectionFiles('SUPERVISION')} actions={attachmentActions} onPreview={setPreview} />
            {permissions.canAddNote ? (
              <div className="note-form">
                <TextAreaField
                  label={t('ملاحظة جديدة', 'New note')}
                  rows={3}
                  value={noteText}
                  onChange={(event) => setNoteText(event.target.value)}
                />
                <Button variant="primary" icon="plus" busy={busy === 'note'} disabled={!noteText.trim()} onClick={addNote}>
                  {t('إضافة الملاحظة', 'Add note')}
                </Button>
              </div>
            ) : null}
          </CollapsibleCard>
        </div>

        <aside className="report__aside" aria-label={t('حالة التقرير', 'Report status')}>
          {permissions.canSubmit ? (
            <Card title={t('متطلبات الإرسال', 'Submit requirements')}>
              <ul className="checklist">
                {checklist.map((item) => (
                  <li key={item.label} className={item.done ? 'is-done' : 'is-todo'}>
                    <Icon name={item.done ? 'check' : 'alert'} size={16} />
                    <span>{t(item.label, item.labelEn)}</span>
                    <span className="sr-only">{item.done ? t('(مكتمل)', '(Complete)') : t('(غير مكتمل)', '(Incomplete)')}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          <Card title={t('التنزيل', 'Download')}>
            <div className="stack">
              <Button icon="eye" onClick={() => window.open(`/projects/${project.id}/reports/${report.id}/print`, '_blank', 'noopener')}>
                {t('معاينة الطباعة', 'Print preview')}
              </Button>
              <Button icon="download" busy={busy === 'xlsx'} onClick={() => run('xlsx', () => api.downloadReport(report.id, 'xlsx'))}>
                {t('Excel (القالب الرسمي)', 'Excel (official template)')}
              </Button>
              <Button icon="file" busy={busy === 'pdf'} onClick={() => run('pdf', () => api.downloadReport(report.id, 'pdf'))}>
                PDF
              </Button>
              <Button
                icon="folder"
                busy={busy === 'zip'}
                disabled={!report.attachments.length}
                onClick={() => run('zip', () => api.downloadReportAttachments(report.id))}
              >
                {t('المرفقات (zip)', 'Attachments (zip)')} · {report.attachments.length}
              </Button>
              {report.workflow !== 'APPROVED' ? <p className="muted small">{t('الملفات تحمل علامة «مسودة» حتى الاعتماد.', 'Files are marked “Draft” until approval.')}</p> : null}
            </div>
          </Card>

          <Card title={t('سير العمل', 'Workflow')}>
            <ol className="timeline">
              <li className={report.preparedByName ? 'is-done' : ''}>
                <strong>{t('الإعداد', 'Preparation')}</strong>
                <span>{report.preparedByName ?? t('لم يبدأ بعد', 'Not started')}</span>
                {report.lastEditedAt ? (
                  <small>
                    {t(`آخر تعديل: ${report.lastEditedByName}`, `Last edit: ${report.lastEditedByName}`)} · {formatDateTime(report.lastEditedAt)}
                  </small>
                ) : null}
              </li>
              <li className={report.submittedAt ? 'is-done' : ''}>
                <strong>{t('الإرسال للاعتماد', 'Submit for approval')}</strong>
                <span>{report.submittedByName ?? '—'}</span>
                {report.submittedAt ? <small>{formatDateTime(report.submittedAt)}</small> : null}
              </li>
              <li className={report.approvedAt ? 'is-done' : ''}>
                <strong>{t('الاعتماد', 'Approval')}</strong>
                <span>{report.approvedByName ?? '—'}</span>
                {report.approvedAt ? <small>{formatDateTime(report.approvedAt)}</small> : null}
              </li>
            </ol>
            <p className="muted small">
              {t('صلاحيتك', 'Your role')}: {ROLE_LABELS[report.myRole]}
            </p>
          </Card>

          <Card
            title={t('التعليقات', 'Comments')}
            actions={
              <span className="muted small">
                {report.notes.length ? t(`${report.notes.length} تعليق`, `${report.notes.length} comments`) : t('لا تعليقات', 'No comments')}
              </span>
            }
          >
            {commentGroups.length ? (
              <div className="comment-groups">
                {commentGroups.map(([section, notes]) => (
                  <div className="comment-group" key={section}>
                    <div className="comment-group__head">
                      <Icon name={SECTION_ICONS[section]} size={14} />
                      <span>{SECTION_LABELS[section]}</span>
                    </div>
                    <ul className="comment-thread">
                      {notes.map((note) => (
                        <li className="comment-message" key={note.id}>
                          <span className="comment-message__avatar">{commentInitials(note.authorName)}</span>
                          <div className="comment-message__body">
                            <div className="comment-message__head">
                              <strong>{note.authorName || '—'}</strong>
                              <time>{formatDateTime(note.createdAt)}</time>
                            </div>
                            <p>{note.text}</p>
                          </div>
                          {canDeleteNote(note.authorId) ? (
                            <button
                              type="button"
                              className="link-button link-button--danger"
                              disabled={busy === `note-${note.id}`}
                              onClick={() => removeNote(note.id)}
                            >
                              {t('حذف', 'Delete')}
                            </button>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              <p className="lines__empty">{t('لا توجد تعليقات على هذا التقرير بعد', 'No comments on this report yet')}</p>
            )}
          </Card>

          {report.audit.length ? (
            <Card title={t('سجل التغييرات', 'Change log')}>
              <ul className="audit">
                {report.audit.slice(0, 40).map((entry) => (
                  <li key={entry.id}>
                    <strong>{AUDIT_LABELS[entry.action]}</strong>
                    <span>
                      {entry.actorName} · {formatDateTime(entry.createdAt)}
                    </span>
                    {entry.note && entry.action !== 'UPDATE' ? <q>{entry.note}</q> : null}
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          <Link to={`/projects/${project.id}`} className="back-link">
            <Icon name="chevronRight" size={16} /> {t('العودة إلى الفهرس', 'Back to the index')}
          </Link>
        </aside>
      </div>

      {dirty && permissions.canEdit ? (
        <div className="savebar" role="status">
          <span>{t('لديك تعديلات غير محفوظة', 'You have unsaved changes')}</span>
          <Button variant="primary" icon="save" busy={busy === 'save'} onClick={() => save()}>
            {t('حفظ (Ctrl+S)', 'Save (Ctrl+S)')}
          </Button>
        </div>
      ) : null}

      {preview ? <PreviewModal attachment={preview} onClose={() => setPreview(null)} /> : null}
      {dialog === 'return' ? (
        <ReturnReportDialog
          reportCode={report.code}
          engineerName={report.preparedByName}
          busy={busy === 'return'}
          onConfirm={confirmReturn}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === 'reopen' ? (
        <NotedTransitionModal
          title={t(`إعادة فتح ${report.code}`, `Reopen ${report.code}`)}
          confirmLabel={t('إعادة فتح', 'Reopen')}
          busy={busy === 'reopen'}
          onConfirm={notedTransition}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </div>
  )
}
